import { z } from "zod";

/**
 * The org app starter, served as ONE self-contained bundle.
 *
 * The starter (`@agent-nexus/apps-starter`) and the component library it
 * depends on (`@agent-nexus/apps-ui`) are both PRIVATE npm packages. A
 * customer's own machine holds no credential for them, so the backend fetches
 * both with the platform's read token and serves a tarball in which the
 * library is vendored as a `file:` dependency. The credential never leaves the
 * backend; `npm install` on the extracted bundle needs no registry auth at all.
 */

/**
 * Dist-tags a caller may name instead of an exact version. Closed on purpose:
 * the value reaches an npm registry URL, so anything outside this list and
 * outside strict semver is refused at the contract.
 */
export const VIBE_APP_STARTER_DIST_TAGS = ["latest"] as const;

/**
 * Strict `MAJOR.MINOR.PATCH` with an optional prerelease — no ranges, no build
 * metadata, no `v` prefix. A range is resolved server-side only, from the
 * starter's own package.json, never from a caller.
 */
export const VIBE_APP_STARTER_EXACT_VERSION_PATTERN =
  /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(-[0-9A-Za-z-]+(\.[0-9A-Za-z-]+)*)?$/;

export const VibeAppStarterVersionSpecSchema = z.union([
  z.enum(VIBE_APP_STARTER_DIST_TAGS),
  z.string().max(64).regex(VIBE_APP_STARTER_EXACT_VERSION_PATTERN, {
    message: "version must be an exact semver (e.g. 0.3.0) or 'latest'"
  })
]);

export type VibeAppStarterVersionSpec = z.infer<typeof VibeAppStarterVersionSpecSchema>;

export const DownloadVibeAppStarterQuerySchema = z.object({
  /** Starter version to bundle. Absent means `latest`. */
  version: VibeAppStarterVersionSpecSchema.optional()
});

export type DownloadVibeAppStarterQuery = z.infer<typeof DownloadVibeAppStarterQuerySchema>;

/**
 * Response headers the bundle is served with, so a client can report what it
 * received without opening the archive. Values are exact resolved versions.
 */
export const VIBE_APP_STARTER_VERSION_HEADER = "x-nexus-apps-starter-version";
export const VIBE_APP_STARTER_UI_VERSION_HEADER = "x-nexus-apps-ui-version";
